import React, { useState, useMemo } from 'react';
import { useI18n } from '../../i18n';

export interface RefDialogCollection {
  id: string;
  name: string;
  document_count: number;
}

export interface RefDialogDocument {
  id: string;
  filename: string;
  status: string;
}

export interface InsertReferenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collections: RefDialogCollection[];
  onLoadDocuments: (collectionId: string) => void;
  documentsByCollection: Record<string, RefDialogDocument[]>;
  loadingCollections: Record<string, boolean>;
  onSelect: (refPath: string) => void;
}

export const InsertReferenceDialog: React.FC<InsertReferenceDialogProps> = ({
  open,
  onOpenChange,
  collections,
  onLoadDocuments,
  documentsByCollection,
  loadingCollections,
  onSelect,
}) => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    if (!search) return collections;
    const q = search.toLowerCase();
    return collections.filter((c) => c.name.toLowerCase().includes(q));
  }, [collections, search]);

  const toggleExpand = (collectionId: string) => {
    const next = new Set(expanded);
    if (next.has(collectionId)) {
      next.delete(collectionId);
    } else {
      next.add(collectionId);
      if (!documentsByCollection[collectionId]) {
        onLoadDocuments(collectionId);
      }
    }
    setExpanded(next);
  };

  const handleSelect = (collectionName: string, docFilename: string) => {
    onSelect(`${collectionName}/${docFilename}`);
    onOpenChange(false);
    setSearch('');
    setExpanded(new Set());
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={() => onOpenChange(false)}
      role="presentation"
    >
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative z-50 w-full max-w-lg bg-background border rounded-lg shadow-lg max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('insertReference.title')}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-sm">{t('insertReference.title')}</h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t('common.close')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div className="p-3 border-b">
          <input
            type="text"
            placeholder={t('insertReference.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <div className="flex-1 overflow-auto p-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">{t('insertReference.noCollections')}</p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((col) => (
                <li key={col.id}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(col.id)}
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                  >
                    <span>{expanded.has(col.id) ? '📂' : '📁'}</span>
                    <span className="flex-1 truncate">{col.name}</span>
                    <span className="text-xs text-muted-foreground">{t('insertReference.documentCount', { count: col.document_count })}</span>
                  </button>
                  {expanded.has(col.id) && (
                    <ul className="ml-6 mt-1 space-y-0.5">
                      {loadingCollections[col.id] ? (
                        <li className="text-xs text-muted-foreground px-2 py-1">{t('common.loading')}</li>
                      ) : (
                        (documentsByCollection[col.id] ?? []).map((doc) => (
                          <li key={doc.id}>
                            <button
                              type="button"
                              onClick={() => handleSelect(col.name, doc.filename)}
                              className="flex items-center gap-2 w-full text-left px-2 py-1 rounded hover:bg-accent text-sm"
                            >
                              <span>📄</span>
                              <span className="flex-1 truncate">{doc.filename}</span>
                              <span className="text-xs text-muted-foreground rounded bg-muted px-1.5 py-0.5">{t('common.select')}</span>
                            </button>
                          </li>
                        ))
                      )}
                      {!loadingCollections[col.id] && (documentsByCollection[col.id] ?? []).length === 0 && (
                        <li className="text-xs text-muted-foreground px-2 py-1">{t('insertReference.noDocuments')}</li>
                      )}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-3 border-t flex justify-end">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 text-sm rounded border hover:bg-muted transition-colors"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};
